"use client";

import { useCallback, useMemo, useState } from "react";
import type {
  AppSchemasSearchCriterionInput,
  SearchDefinitionSchemaInput,
  SearchFiltersSchema,
} from "@/lib/api/model";
import { canonicalNetworkFilter } from "@/features/network/lib/filter";
import {
  STANDARD_POOL_ID,
  normalizePoolIds,
} from "@/features/talent-pools/lib/filter-categories";

/**
 * Orval splits the backend's single `SearchDefinitionSchema` into an `Input`
 * (request) and an `Output` (response) type because pydantic serialises the
 * criteria differently on each side. Both are `{ filters?, criteria?,
 * rerank_query? }` with `{ label, description }` criteria, so the response
 * shape assigns to the request shape structurally. The request shape is the
 * one runs are posted with, so it is the canonical draft type here.
 */
export type SearchDefinition = SearchDefinitionSchemaInput;
export type SearchCriterion = AppSchemasSearchCriterionInput;

type Def = SearchDefinition;

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value && typeof value === "object") {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, k) => {
        const v = (value as Record<string, unknown>)[k];
        if (
          v === null ||
          v === undefined ||
          v === "" ||
          (Array.isArray(v) && v.length === 0)
        )
          return acc;
        acc[k] = sortKeys(v);
        return acc;
      }, {});
  }
  return typeof value === "string" ? value.trim() : value;
}

/**
 * Top-level `SearchFiltersSchema` fields that the backend fills with a
 * non-null default on read. Mirrors `backend/app/schemas/search.py`; the
 * backend's `canonical_filters` drops a field sitting at its default because
 * the query builders fall back to the same value when the key is absent.
 * Keep in sync when a default there changes.
 */
export const FILTER_DEFAULTS: Record<string, unknown> = {
  jt_filter_type: "c",
  distance: "25mi",
  company_tag_filter_type: "cp",
  funding_filter_type: "cp",
  investor_filter_type: "cp",
  funding_scope: "any",
  industry_filter_type: "cp",
  min_degree: "none",
  // A status narrows on its own (people holding an active certification), so
  // it is search intent; only the `any` default is dropped.
  certification_status: "any",
  exclude_org_companies: false,
  // On by default: the org's People Exclusions apply to search unless unticked.
  exclude_org_profiles: true,
  exclude_dnc_companies: false,
  exclude_dnc_countries: false,
};

/**
 * Per-item defaults of the nested filter schemas, keyed by the top-level
 * `SearchFiltersSchema` field that holds them. The backend re-fills these on
 * every read (`SkillFilterSchema`, `FilterValueSchema`, `LocationFilterSchema`,
 * `SimpleFilterEntitySchema`, `ExperienceConstraintSchema`,
 * `EducationConstraintSchema`, `HideViewedFilterSchema`,
 * `HideReviewedFilterSchema` in
 * `backend/app/schemas/search.py`), so a draft item `{ text: "go" }` comes
 * back as `{ text: "go", mode: "include", required: false }`. Stripping them
 * on both sides keeps a server round-trip from reading as dirty. Keep in
 * sync when a default there changes. (`FilterValueSchema.scope` defaults to
 * `None` and is already dropped as an empty value.)
 */
const FILTER_VALUE_DEFAULTS: Record<string, unknown> = {
  mode: "include",
  required: false,
};
const SKILL_DEFAULTS: Record<string, unknown> = {
  mode: "include",
  required: false,
};
const LOCATION_DEFAULTS: Record<string, unknown> = {
  mode: "include",
  required: false,
  scope: "cp",
};
const SIMPLE_ENTITY_DEFAULTS: Record<string, unknown> = {
  mode: "include",
  required: false,
};
const CONSTRAINT_DEFAULTS: Record<string, unknown> = { mode: "include" };
const HIDE_VIEWED_DEFAULTS: Record<string, unknown> = {
  scope: "none",
  by: "anyone",
  time_range: "all_time",
};
const HIDE_REVIEWED_DEFAULTS: Record<string, unknown> = {
  ...HIDE_VIEWED_DEFAULTS,
  // Sorted, like the canonical form (backend `canonical_filters` matches).
  decisions: ["maybe", "no", "yes"],
};

export const NESTED_FILTER_DEFAULTS: Record<string, Record<string, unknown>> =
  {
    // FilterValueSchema
    job_titles: FILTER_VALUE_DEFAULTS,
    past_job_titles: FILTER_VALUE_DEFAULTS,
    job_title_levels: FILTER_VALUE_DEFAULTS,
    job_title_roles: FILTER_VALUE_DEFAULTS,
    companies: FILTER_VALUE_DEFAULTS,
    company_funding_stages: FILTER_VALUE_DEFAULTS,
    industries: FILTER_VALUE_DEFAULTS,
    universities: FILTER_VALUE_DEFAULTS,
    full_names: FILTER_VALUE_DEFAULTS,
    // LocationFilterSchema
    locations: LOCATION_DEFAULTS,
    past_locations: LOCATION_DEFAULTS,
    company_locations: LOCATION_DEFAULTS,
    university_locations: LOCATION_DEFAULTS,
    // SkillFilterSchema
    skills: SKILL_DEFAULTS,
    // SimpleFilterEntitySchema
    company_tags: SIMPLE_ENTITY_DEFAULTS,
    investors: SIMPLE_ENTITY_DEFAULTS,
    majors: SIMPLE_ENTITY_DEFAULTS,
    languages: SIMPLE_ENTITY_DEFAULTS,
    highlight_types: SIMPLE_ENTITY_DEFAULTS,
    power_filters: SIMPLE_ENTITY_DEFAULTS,
    // ExperienceConstraintSchema / EducationConstraintSchema
    experience_constraints: CONSTRAINT_DEFAULTS,
    education_constraints: CONSTRAINT_DEFAULTS,
    // HideViewedFilterSchema (a single object, not a list)
    hide_viewed: HIDE_VIEWED_DEFAULTS,
    hide_reviewed: HIDE_REVIEWED_DEFAULTS,
  };

/**
 * Filter keys that carry no search intent on their own: match-mode modifiers
 * (`MODIFIER_KEYS`) and viewer / org-policy / UI state (`INTENT_FREE_KEYS`)
 * from `backend/app/services/search/filter_render.py`. A definition whose
 * only filters are these is empty for the purpose of running a search, but
 * changing one is still a real edit, so they count towards `dirty`.
 */
export const INTENT_FREE_FILTER_KEYS: ReadonlySet<string> = new Set([
  // MODIFIER_KEYS
  "jt_filter_type",
  "distance",
  "company_tag_filter_type",
  "funding_filter_type",
  "investor_filter_type",
  "industry_filter_type",
  "funding_scope",
  // INTENT_FREE_KEYS
  "hide_viewed",
  "hide_reviewed",
  "exclude_org_profiles",
  "exclude_dnc_companies",
  "exclude_dnc_countries",
  "profile_ids",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/** `item` minus every key sitting at its schema default. */
function stripDefaults(
  item: Record<string, unknown>,
  defaults: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(item)) {
    if (k in defaults && JSON.stringify(v) === JSON.stringify(defaults[k])) {
      continue;
    }
    out[k] = v;
  }
  return out;
}

/**
 * `pool_membership` mirrors the backend's `_canonical_pool_membership`:
 * sorted, de-duplicated id lists with empty sides dropped; an object left
 * with no ids is no filter at all.
 */
function canonicalPoolMembership(
  value: unknown,
): Record<string, string[]> | undefined {
  if (!isRecord(value)) return undefined;
  const out: Record<string, string[]> = {};
  for (const side of ["exclude", "include"] as const) {
    const ids = value[side];
    if (!Array.isArray(ids)) continue;
    const unique = [...new Set(ids.map(String))].sort();
    if (unique.length) out[side] = unique;
  }
  return Object.keys(out).length ? out : undefined;
}

function canonicalFilters(filters: SearchFiltersSchema | undefined) {
  const sorted = sortKeys(filters ?? {}) as Record<string, unknown>;
  for (const key of Object.keys(sorted)) {
    if (key === "pool_membership") {
      const pm = canonicalPoolMembership(sorted[key]);
      if (pm) sorted[key] = pm;
      else delete sorted[key];
      continue;
    }
    if (key === "network") {
      // Unlike the others, a network filter at its defaults still narrows
      // (anyone the team knows), so it is kept as `{}`.
      const network = canonicalNetworkFilter(sorted[key]);
      if (network) sorted[key] = network;
      else delete sorted[key];
      continue;
    }
    if (
      key in FILTER_DEFAULTS &&
      JSON.stringify(sorted[key]) === JSON.stringify(FILTER_DEFAULTS[key])
    ) {
      delete sorted[key];
      continue;
    }
    const nested = NESTED_FILTER_DEFAULTS[key];
    if (!nested) continue;
    if (key === "hide_reviewed" && isRecord(sorted[key])) {
      // Order and repeats of the decision subset carry no meaning.
      const item = sorted[key] as Record<string, unknown>;
      if (Array.isArray(item.decisions)) {
        sorted[key] = {
          ...item,
          decisions: [...new Set(item.decisions.map(String))].sort(),
        };
      }
    }
    const value = sorted[key];
    if (Array.isArray(value)) {
      sorted[key] = value.map((item) =>
        isRecord(item) ? stripDefaults(item, nested) : item,
      );
    } else if (isRecord(value)) {
      const stripped = stripDefaults(value, nested);
      // An object left with nothing but defaults (e.g. `hide_viewed` at
      // scope "none") carries no intent at all.
      if (Object.keys(stripped).length === 0) delete sorted[key];
      else sorted[key] = stripped;
    }
  }
  return sorted as SearchFiltersSchema;
}

/**
 * Normalised form used for equality: keys sorted, empty / null / blank values
 * dropped, strings trimmed, top-level filters equal to their backend default
 * dropped, criteria without both a label and a description removed, a
 * blank `rerank_query` collapsed to `null`, and `pool_ids` sorted and kept
 * only when it is not the standard pool alone. Mirrors the backend's
 * `canonical_definition` so a server round-trip (which re-fills defaults)
 * compares equal to the draft that produced it.
 */
export function canonicalDefinition(d: Def): Def {
  const rq = (d.rerank_query ?? "").trim();
  const pools = normalizePoolIds(
    d.pool_ids?.length ? d.pool_ids : [STANDARD_POOL_ID],
  );
  const standardOnly = pools.length === 1 && pools[0] === STANDARD_POOL_ID;
  return {
    filters: canonicalFilters(d.filters),
    criteria: (d.criteria ?? [])
      .map((c) => ({
        label: c.label.trim(),
        description: c.description.trim(),
      }))
      .filter((c) => c.label && c.description),
    rerank_query: rq ? rq : null,
    ...(standardOnly ? {} : { pool_ids: pools }),
  };
}

/**
 * Body to post for a run: the canonical definition, but with `pool_ids`
 * always explicit when the draft names pools. A run that names no pools
 * keeps the search's stored pools (or, for a new search, the org's
 * defaults), so dropping the standard-only list would not select it.
 */
export function definitionForPost(d: Def): Def {
  const canonical = canonicalDefinition(d);
  return d.pool_ids?.length
    ? { ...canonical, pool_ids: normalizePoolIds(d.pool_ids) }
    : canonical;
}

/** Stable string form of a definition's canonical shape, for equality. */
export function definitionKey(d: Def): string {
  return JSON.stringify(canonicalDefinition(d));
}

export function definitionDirty(a: Def, b: Def): boolean {
  return definitionKey(a) !== definitionKey(b);
}

export function isEmptyDefinition(d: Def): boolean {
  const c = canonicalDefinition(d);
  const hasFilterIntent = Object.entries(c.filters as object).some(
    ([key, value]) =>
      !INTENT_FREE_FILTER_KEYS.has(key) &&
      // Only `include` narrows to a set of people; exclude-only pool or
      // network filters are viewer/org state like hide_viewed.
      (key !== "pool_membership" || (isRecord(value) && "include" in value)) &&
      (key !== "network" || (isRecord(value) && value.mode !== "exclude")),
  );
  return !hasFilterIntent && (c.criteria ?? []).length === 0 && !c.rerank_query;
}

export const EMPTY_DEFINITION: Def = {
  filters: {},
  criteria: [],
  rerank_query: null,
};

/**
 * Definition for a search started from a filter set alone (hero Run button,
 * a company's "Explore profiles", the assistant's "Continue" with the current
 * filters): no criteria, no rerank query, canonicalised so the posted body
 * matches what the server stores. `poolIds` (the picker's selection) is
 * posted explicitly; without it the backend uses the org's default pools.
 */
export function buildDefinitionFromFilters(
  filters: SearchFiltersSchema | undefined,
  poolIds?: string[] | null,
): Def {
  return definitionForPost({
    filters: filters ?? {},
    criteria: [],
    rerank_query: null,
    pool_ids: poolIds?.length ? poolIds : undefined,
  });
}

/**
 * The pools a stored definition searches when it names none: the org's
 * default pools for a search that has never held a definition (created
 * empty, nothing run yet), else the standard pool alone (legacy searches).
 */
export function basePoolIdsFor(
  server: Def | null,
  defaultPoolIds: string[] | null,
): string[] | null {
  if (server?.pool_ids?.length) return normalizePoolIds(server.pool_ids);
  if (!server || isEmptyDefinition(server)) return defaultPoolIds;
  return [STANDARD_POOL_ID];
}

interface SeedState {
  searchId: string;
  seedKey: string;
  serverKey: string;
  /** `postedKey` whose server confirmation has already been consumed. */
  confirmedPost: string | null;
}

/**
 * Local, unsaved edits to a search definition. Seeded from `server` (the
 * saved search's current definition). `dirty` is true when the draft differs
 * from `server` after canonicalisation; Save is Run, so callers gate the run
 * button on it.
 *
 * `basePoolIds` (see `basePoolIdsFor`) fills `pool_ids` on both sides when a
 * definition names none, so the returned draft always carries the pools the
 * picker shows and a run posts them explicitly.
 *
 * Re-seed rules, applied during render:
 *
 * - The search id changed: seed from the new search's definition.
 * - `seedKey` changed: re-seed from `server` even though it is unchanged. The
 *   results provider bumps a counter after a run whose response handed back
 *   the same run for the same definition (nothing in the cache moved, yet
 *   the draft now matches what the server holds), and only when the draft
 *   still equals what was posted, so this never discards edits.
 * - The canonical server definition changed and equals `postedKey` (the
 *   definition this tab posted with its last run): the server merely
 *   confirmed our post, so the draft is left alone and only the seed marker
 *   moves. `dirty` is then right by construction: false when nothing was
 *   typed since the click (draft equals server), true when an edit landed
 *   between click and response. Each posted key is consumed once; the same
 *   definition arriving again later (a teammate's run) re-seeds like any
 *   other server change.
 * - Any other change to the canonical server definition (agent apply, a
 *   teammate's run with a different definition): re-seed from `server`.
 * - A refetch that returns the same definition: nothing happens, in-progress
 *   edits stay.
 */
export function useSearchDefinitionDraft(
  searchId: string | null,
  server: Def | null,
  seedKey: string | null = null,
  postedKey: string | null = null,
  basePoolIds: string[] | null = null,
) {
  const [draft, setDraft] = useState<Def>(server ?? EMPTY_DEFINITION);
  const serverKey = useMemo(
    () => definitionKey(server ?? EMPTY_DEFINITION),
    [server],
  );

  // Re-seed during render rather than in an effect so the previous search's
  // draft never reaches the screen for a frame when the search switches.
  // `draft` (the state) still holds the old value in this render, so the
  // freshly seeded definition is used for everything derived below.
  const key = {
    searchId: searchId ?? "new",
    seedKey: seedKey ?? "",
    serverKey,
  };
  const [seeded, setSeeded] = useState<SeedState>({
    ...key,
    confirmedPost: null,
  });
  let current = draft;
  if (
    seeded.searchId !== key.searchId ||
    seeded.seedKey !== key.seedKey ||
    seeded.serverKey !== key.serverKey
  ) {
    const confirmsPost =
      seeded.searchId === key.searchId &&
      seeded.seedKey === key.seedKey &&
      postedKey !== null &&
      serverKey === postedKey &&
      seeded.confirmedPost !== postedKey;
    if (confirmsPost) {
      setSeeded({ ...key, confirmedPost: postedKey });
    } else {
      // Consumed posts are remembered across re-seeds of the same search so
      // the same definition coming back later is not mistaken for our post.
      setSeeded({
        ...key,
        confirmedPost:
          seeded.searchId === key.searchId ? seeded.confirmedPost : null,
      });
      current = server ?? EMPTY_DEFINITION;
      setDraft(current);
    }
  }

  const setFilters = useCallback(
    (filters: SearchFiltersSchema) => setDraft((d) => ({ ...d, filters })),
    [],
  );
  const updateFilter = useCallback(
    <K extends keyof SearchFiltersSchema>(
      key: K,
      value: SearchFiltersSchema[K],
    ) =>
      setDraft((d) => ({
        ...d,
        filters: { ...(d.filters ?? {}), [key]: value },
      })),
    [],
  );
  const setCriteria = useCallback(
    (criteria: SearchCriterion[]) => setDraft((d) => ({ ...d, criteria })),
    [],
  );
  const setRerankQuery = useCallback(
    (rerank_query: string | null) =>
      setDraft((d) => ({ ...d, rerank_query })),
    [],
  );
  const setPoolIds = useCallback(
    (pool_ids: string[]) => setDraft((d) => ({ ...d, pool_ids })),
    [],
  );
  const reset = useCallback(
    () => setDraft(server ?? EMPTY_DEFINITION),
    [server],
  );

  const resolved = useMemo(
    () =>
      current.pool_ids?.length || !basePoolIds
        ? current
        : { ...current, pool_ids: basePoolIds },
    [current, basePoolIds],
  );
  const dirty = useMemo(() => {
    const base = server ?? EMPTY_DEFINITION;
    return definitionDirty(
      resolved,
      base.pool_ids?.length || !basePoolIds
        ? base
        : { ...base, pool_ids: basePoolIds },
    );
  }, [resolved, server, basePoolIds]);
  const isEmpty = useMemo(() => isEmptyDefinition(current), [current]);

  return {
    draft: resolved,
    setFilters,
    updateFilter,
    setCriteria,
    setRerankQuery,
    setPoolIds,
    reset,
    dirty,
    isEmpty,
  };
}
